import * as React from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type LabelStyle = "floating" | "top" | "none";

export interface CustomTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value"> {
  labelStyle?: LabelStyle;
  label?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  tooltip?: string;
  error?: string;
  wrapperClassName?: string;
}

const CustomTextarea = React.forwardRef<HTMLTextAreaElement, CustomTextareaProps>(
  (
    {
      className,
      labelStyle = "floating",
      label,
      value: propValue,
      onChange,
      tooltip,
      error,
      wrapperClassName,
      disabled,
      placeholder,
      id,
      rows = 3,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState("");
    const [isFocused, setIsFocused] = React.useState(false);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Sync internal value with prop value when not focused
    React.useEffect(() => {
      if (!isFocused) {
        if (propValue === undefined || propValue === null) {
          setInternalValue("");
        } else {
          setInternalValue(String(propValue));
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propValue]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const currentValue = e.target.value;
      setInternalValue(currentValue);

      if (!onChange) return;

      if (currentValue === "") {
        onChange(undefined);
        return;
      }

      onChange(currentValue);
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    const handleWrapperClick = () => {
      if (!disabled) {
        setIsFocused(true);
        textareaRef.current?.focus();
      }
    };

    // Combine refs
    const setRefs = React.useCallback(
      (element: HTMLTextAreaElement | null) => {
        (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = element;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    const hasValue = internalValue !== "" && internalValue !== undefined && internalValue !== null;

    const renderTextarea = () => (
      <Textarea
        {...props}
        ref={setRefs}
        id={id}
        rows={rows}
        disabled={disabled}
        placeholder={labelStyle === "floating" && label ? undefined : placeholder}
        className={cn(
          "peer min-h-0 w-full resize-none border-0 bg-transparent p-0 shadow-none focus:ring-0 focus:outline-none focus-visible:ring-0",
          labelStyle === "floating" && hasValue && label && "translate-y-1.5",
          className,
        )}
        value={internalValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );

    // Top label style - simple layout
    if (labelStyle === "top") {
      return (
        <div className={cn("flex flex-col gap-2", wrapperClassName)}>
          {label && (
            <div className="flex items-center gap-2">
              <Label htmlFor={id}>{label}</Label>
              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="size-4 cursor-help text-blue-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-64 text-sm">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
          <div
            className={cn(
              "relative flex w-full rounded-lg border bg-white px-3 py-2 transition-all duration-200",
              {
                "border-blue-600": isFocused,
                "border-red-500": error,
                "border-gray-300": !isFocused && !error,
                "pointer-events-none bg-gray-50": disabled,
                "hover:border-gray-400": !disabled && !isFocused && !error,
              },
            )}
            onClick={handleWrapperClick}
          >
            {renderTextarea()}
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }

    // Floating label style (default)
    return (
      <div className={cn("flex flex-col gap-2", wrapperClassName)}>
        <div
          className={cn(
            "relative flex w-full rounded-lg border bg-white px-4 pt-5 pb-2 transition-all duration-200",
            {
              "border-blue-600": isFocused,
              "border-red-500": error,
              "border-gray-300": !isFocused && !error,
              "pointer-events-none bg-gray-50": disabled,
              "hover:border-gray-400": !disabled && !isFocused && !error,
            },
          )}
          onClick={handleWrapperClick}
        >
          {label && (
            <Label
              className={cn(
                "pointer-events-none absolute top-2 left-4 text-sm text-gray-500 transition-all duration-200 ease-in-out",
                {
                  "text-xs": hasValue || isFocused,
                  "translate-y-2 text-sm": !hasValue && !isFocused,
                },
              )}
              htmlFor={id}
            >
              {label}
            </Label>
          )}

          {renderTextarea()}

          {tooltip && (
            <div className="absolute top-2 right-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="size-4 cursor-help text-blue-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-64 text-sm">{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  },
);

CustomTextarea.displayName = "CustomTextarea";

export { CustomTextarea };
